import type { TimelineEvent } from './candidateDetailTimelines';

type Props = {
  /** Omit when the parent panel already shows the section title (e.g. collapsible header). */
  title?: string;
  events: TimelineEvent[];
  emptyMessage?: string;
  /** Default (false): newest-first, "Now" at top, "Earlier" before older rows.
   *  ascending (true): oldest-first, "Current" at bottom with heartbeat pulse. */
  ascending?: boolean;
};

export function CandidateVerticalTimeline({
  title,
  events,
  emptyMessage = 'No events yet.',
  ascending = false,
}: Props) {
  const ordered = ascending ? [...events].reverse() : events;
  const lastIdx = ordered.length - 1;

  return (
    <div className="space-y-3">
      {title ? (
        <p className="text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary">{title}</p>
      ) : null}
      {ordered.length === 0 ? (
        <p className="text-xs vscode-text-tertiary">{emptyMessage}</p>
      ) : (
        <ul className="relative pl-5 border-l-2 border-[color-mix(in_srgb,var(--brand-500)_50%,var(--vscode-border-primary))] space-y-6">
          {ordered.map((e, index) => {
            const isCurrentEvent = ascending ? index === lastIdx : index === 0;
            const showEarlierLabel = !ascending && index === 1;

            return (
              <li key={e.id} className="relative">
                {showEarlierLabel ? (
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] vscode-text-tertiary mb-3 -mt-1">
                    Earlier
                  </p>
                ) : null}

                {/* Circle dot — larger for clarity, heartbeat pulse on current */}
                <span className="absolute -left-[21px] top-1" aria-hidden>
                  {isCurrentEvent ? (
                    <span className="relative flex h-5 w-5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-brand-400/40 animate-ping" />
                      <span className="relative inline-flex h-5 w-5 rounded-full bg-brand-500 border-[3px] border-[var(--vscode-bg-elevated)] shadow-md" />
                    </span>
                  ) : (
                    <span className="inline-flex h-4 w-4 rounded-full bg-[var(--vscode-bg-elevated)] border-2 border-brand-500/70 shadow-sm" />
                  )}
                </span>

                {/* Label above current event */}
                {isCurrentEvent ? (
                  <p className="text-[10px] font-bold uppercase tracking-wider text-brand-500 mb-1">
                    {ascending ? 'Current' : 'Now'}
                  </p>
                ) : null}

                <p className="text-sm font-medium vscode-text-primary leading-snug">{e.primary}</p>
                {e.secondary ? (
                  <p className="text-xs vscode-text-secondary mt-0.5 truncate">{e.secondary}</p>
                ) : null}
                <p className="text-[11px] vscode-text-tertiary mt-1 tabular-nums">{e.timeLabel}</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
